// KD-3735 — the organization this browser tab acts as, sent to the API as the
// X-Organization-Id header. Kept in sessionStorage so two tabs can work in two
// organizations. Nothing stored = the API default (the first organization).
// For a group member, choosing the group itself means "all its organizations".

const STORAGE_KEY = 'actingOrganizationId';
export const ORGANIZATION_ID_HEADER = 'X-Organization-Id';
export const ORGANIZATION_REQUIRED_EVENT = 'koob:organization-required';

export const getActingOrganizationId = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

export const setActingOrganizationId = (organizationId) => {
  try {
    if (organizationId) {
      sessionStorage.setItem(STORAGE_KEY, String(organizationId));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage blocked (private mode): the tab stays on the API default.
  }
};

/** Switches the whole app to `organizationId` (raw id; null = default). */
export const switchActingOrganization = (organizationId) => {
  setActingOrganizationId(organizationId);
  // ponytail: a reload refetches every screen and cache in one go; swap for a
  // query-cache reset if reloads ever get slow.
  window.location.reload();
};

/** axios request interceptor step: adds the header when a choice is stored. */
export const addActingOrganizationHeader = (config) => {
  const organizationId = getActingOrganizationId();
  if (organizationId) {
    config.headers[ORGANIZATION_ID_HEADER] = organizationId;
  }
  return config;
};

/**
 * The organization of `user` (GET /api/v1/user) this tab acts as: the stored
 * choice when the user still has it, else the first one, like the API.
 */
export const actingOrganizationOf = (user) => {
  const organizations = user?.organizations ?? [];
  const chosen = getActingOrganizationId();
  return (
    organizations.find((org) => String(org.organizationId) === chosen) ??
    organizations[0]
  );
};

/**
 * axios response error step for the two organization errors. Returns true when
 * the error is handled here and must not reach the session logic:
 * - 401 on a request that carried the header (other than an expired session):
 *   the stored organization is no longer the user's; drop it and reload;
 * - 400 ORGANIZATION_REQUIRED: ask which organization (the app's prompt
 *   listens to ORGANIZATION_REQUIRED_EVENT). Still rejected for the caller.
 */
export const handleOrganizationError = (error) => {
  const { status, data } = error?.response ?? {};
  const sentHeader = Boolean(error?.config?.headers?.[ORGANIZATION_ID_HEADER]);
  if (
    status === 401 &&
    sentHeader &&
    data?.code !== 'AUTHENTICATION_REQUIRED'
  ) {
    switchActingOrganization(null);
    return true;
  }
  if (status === 400 && data?.code === 'ORGANIZATION_REQUIRED') {
    window.dispatchEvent(
      new CustomEvent(ORGANIZATION_REQUIRED_EVENT, {
        detail: data.details?.organizations ?? [],
      }),
    );
  }
  return false;
};
